import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DeleteOutline } from '@mui/icons-material';

import { Button, ConfirmModal, Copy, Input } from '..';
import { useIsMounted } from '../../hooks';
import type { InboundPhoneNumberType as PhoneType } from '../../api';

type PhonesInputProps = {
  values: PhoneType[];
  loading?: boolean;
  disabled?: boolean;

  onDelete?: (phone: PhoneType) => Promise<void>;
  onAdd?: (phoneNumber: string) => Promise<string | undefined>;
};

export const PhonesInput: React.FC<PhonesInputProps> = ({
  loading,
  values,
  disabled: defaultDisabled,

  onAdd,
  onDelete,
}) => {
  const isMounted = useIsMounted();
  const disabled = defaultDisabled || loading;
  const [localValues, setValues] = useState(values || []);
  const [pendingDelete, setPendingDelete] = useState<PhoneType | null>(null);
  const handleCloseDeleteModal = () => setPendingDelete(null);
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;

    await onDelete?.(pendingDelete);
    if (!isMounted.current) return;

    setValues(old => old.filter(item => item.id !== pendingDelete.id));
    setPendingDelete(null);
  };

  return (
    <>
      <div className="flex flex-col gap-y-md mb-md">
        {localValues.map((phone, index) => {
          const exists = values?.some(v => v.id === phone.id);

          return (
            <div key={phone.id} className="flex flex-row gap-x-lg items-center">
              <Input
                key={index}
                disabled={disabled}
                value={phone.phoneNumber}
                onChange={phoneNumber => {
                  setValues(old =>
                    old.map(o =>
                      o.id === phone.id ? { ...phone, phoneNumber } : o
                    )
                  );
                }}
              />
              {exists ? (
                <div className="flex flex-row gap-x-md items-center">
                  <Copy
                    label="Copy ID"
                    text={phone.id || ''}
                    className="text-buttonPrimary border border-buttonPrimary rounded-xl px-xs py-xs gap-x-xs hover:bg-select"
                    iconClassName="text-small"
                    labelClassName="text-small"
                  />
                  <Button
                    type="primary"
                    loading={loading}
                    disabled={disabled}
                    icon={<DeleteOutline className="text-small" />}
                    onClick={() => setPendingDelete(phone)}
                  />
                </div>
              ) : (
                <Button
                  type="light"
                  label="Save"
                  onClick={async () => {
                    const newId = await onAdd?.(phone?.phoneNumber);
                    if (!newId || !isMounted.current) return;

                    setValues(old =>
                      old.map(o =>
                        o.id === phone.id ? { ...o, id: newId } : o
                      )
                    );
                  }}
                  disabled={disabled || !phone?.phoneNumber?.length}
                />
              )}
            </div>
          );
        })}
      </div>
      <Button
        label="Add new"
        disabled={disabled}
        onClick={() =>
          setValues(old => [...old, { id: uuid(), phoneNumber: '' }])
        }
      />
      <ConfirmModal
        open={!!pendingDelete}
        title="Delete phone number"
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        confirmButtonLabel="Delete"
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
        saving={loading}
      >
        <p className="text-medium text-center">
          Are you sure you want to delete this phone number? This action cannot
          be undone.
        </p>
      </ConfirmModal>
    </>
  );
};
